import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FC,
  type ReactNode
} from "react";

import type { BoardSource } from "@/features/automations/board/BoardEntry";
import { useRealtimeEvent } from "@/lib/realtime/RealtimeProvider";

import { confirmDiscardDocuments } from "../../draftEditor/documentState";
import {
  activate,
  activeArtifact,
  closeArtifact,
  closeOthers,
  EMPTY_OPEN_ARTIFACTS,
  openArtifact,
  pruneMissing,
  restoreOpen,
  retitle,
  type OpenArtifactsState
} from "../openArtifacts";
import {
  artifactKey,
  type Artifact,
  type IdeSelection,
  type InvalidateListener,
  type InvalidateTarget
} from "../types";
import { ideLayoutStore } from "../layout/ideLayoutStore";
import { invalidateCase } from "./caseInvalidation";

/**
 * Which side of the Case Automations tab to land on: the firm's automations
 * or CaseBlink's case preparation. `nonce` tells a repeat request from the last.
 */
export type WorkflowsEntryRequest = { source: BoardSource; nonce: number };

export type CaseIdeContextValue = {
  individualId: string;

  // Center pane
  tabs: Artifact[];
  activeKey: string | null;
  activeArtifact: Artifact | null;
  open: (artifact: Artifact) => void;
  close: (key: string) => void;
  closeOtherTabs: (key: string) => void;
  setActive: (key: string) => void;
  renameTab: (key: string, title: string) => void;
  pruneTabs: (exists: (artifact: Artifact) => boolean) => void;

  // Selection the chat sends with each turn
  selection: IdeSelection;
  setSelectedText: (text: string | null) => void;
  setSelectedNodeIds: (ids: string[]) => void;

  // Refetch bus: tools and `data-invalidate` parts fan out to the trees
  invalidate: (targets: InvalidateTarget[]) => void;
  onInvalidate: (listener: InvalidateListener) => () => void;

  // Chat composer hooks: trees push mentions, the empty state seeds prompts
  requestMention: (artifact: Artifact) => void;
  onMentionRequest: (listener: (artifact: Artifact) => void) => () => void;
  requestPrompt: (text: string) => void;
  onPromptRequest: (listener: (text: string) => void) => () => void;
  /** The plan pane's Build button asks the chat to build a plan. */
  requestPlanBuild: (planId: string) => void;
  onPlanBuildRequest: (listener: (planId: string) => void) => () => void;
  /** A pane's button sends a message to Casey (the packet's Build Packet). */
  requestSend: (text: string) => void;
  onSendRequest: (listener: (text: string) => void) => () => void;
  /** The board's Run button asks the chat to run one of the firm's automations. */
  requestRunAutomation: (skillId: string) => void;
  onRunAutomationRequest: (listener: (skillId: string) => void) => () => void;

  /**
   * Which side the Case Automations tab should show next. State rather than a
   * bus so the tab sees a request made before it mounted; the tab clears it
   * when consumed. `requestWorkflowsEntry(null)` drops a pending request, so
   * a plain open keeps the tab's own default (or the attorney's last choice).
   */
  workflowsEntry: WorkflowsEntryRequest | null;
  requestWorkflowsEntry: (source: BoardSource | null) => void;
  clearWorkflowsEntry: (nonce: number) => void;

  /**
   * "Show in document": open a draft and select `text` in it once the editor
   * is up. State rather than a bus so a pane that mounts after the request
   * still sees it; the pane clears it when consumed.
   */
  revealInDraft: (
    draftId: string,
    target: string | RevealTarget,
    title?: string
  ) => void;
  reveal: RevealRequest | null;
  clearReveal: (nonce: number) => void;
};

/**
 * What to land on: a tracked change by its `w:id` when known (the editor can
 * select the exact revision), with the text as the fallback once the change
 * has been resolved and only its words remain.
 */
export type RevealTarget = { text: string; changeId?: string };

export type RevealRequest = RevealTarget & { draftId: string; nonce: number };

const CaseIdeContext = createContext<CaseIdeContextValue | null>(null);

const tabsStorageKey = (individualId: string) => `caseIde.tabs.${individualId}`;

function loadTabs(individualId: string): OpenArtifactsState {
  try {
    const raw = window.localStorage.getItem(tabsStorageKey(individualId));
    if (!raw) return EMPTY_OPEN_ARTIFACTS;
    return restoreOpen(JSON.parse(raw), individualId);
  } catch {
    return EMPTY_OPEN_ARTIFACTS;
  }
}

type Props = {
  individualId: string;
  children: ReactNode;
};

export const CaseIdeProvider: FC<Props> = ({ individualId, children }) => {
  const [openState, setOpenState] = useState<OpenArtifactsState>(() =>
    loadTabs(individualId)
  );
  const [selectedText, setSelectedText] = useState<string | null>(null);
  const [selectedNodeIds, setSelectedNodeIds] = useState<string[]>([]);
  const invalidateListeners = useRef(new Set<InvalidateListener>());
  const mentionListeners = useRef(new Set<(artifact: Artifact) => void>());
  const promptListeners = useRef(new Set<(text: string) => void>());
  const planBuildListeners = useRef(new Set<(planId: string) => void>());
  const sendListeners = useRef(new Set<(text: string) => void>());
  const runAutomationListeners = useRef(new Set<(skillId: string) => void>());

  useEffect(() => {
    try {
      window.localStorage.setItem(
        tabsStorageKey(individualId),
        JSON.stringify(openState)
      );
    } catch {
      // Storage full or disabled: tabs simply are not remembered.
    }
  }, [individualId, openState]);

  const open = useCallback((artifact: Artifact) => {
    ideLayoutStore.showDocument();
    setOpenState((s) => openArtifact(s, artifact));
    setSelectedText(null);
  }, []);
  // Closing a tab with unsaved edits asks first; the editor registered the
  // document under its artifact key (see `documentState.ts`).
  const close = useCallback((key: string) => {
    if (!confirmDiscardDocuments(key)) return;
    setOpenState((s) => closeArtifact(s, key));
  }, []);
  const closeOtherTabs = useCallback((key: string) => {
    setOpenState((s) => {
      const others = s.tabs
        .map((tab) => artifactKey(tab))
        .filter((tabKey) => tabKey !== key);
      if (!others.every((tabKey) => confirmDiscardDocuments(tabKey))) return s;
      return closeOthers(s, key);
    });
  }, []);
  const setActive = useCallback((key: string) => {
    setOpenState((s) => activate(s, key));
    setSelectedText(null);
  }, []);
  const renameTab = useCallback(
    (key: string, title: string) => setOpenState((s) => retitle(s, key, title)),
    []
  );
  const pruneTabs = useCallback(
    (exists: (artifact: Artifact) => boolean) =>
      setOpenState((s) => pruneMissing(s, exists)),
    []
  );

  const invalidate = useCallback(
    (targets: InvalidateTarget[]) => {
      if (!targets.length) return;
      invalidateListeners.current.forEach((listener) => listener(targets));
      // The case header mounts above this provider; reach it through the bus.
      invalidateCase(individualId, targets);
    },
    [individualId]
  );
  // A drafting run that finishes (or a waiting draft that starts) in the
  // background says so on the stream; the tree, panes and strips refetch.
  useRealtimeEvent<{ individual_id: string; draft_id: string }>(
    "draft.status",
    (data) => {
      if (data?.individual_id === individualId) invalidate(["drafts"]);
    }
  );
  // Text extraction finished (the "no content" warning can clear) or a file
  // landed from somewhere else; the files tree refetches.
  useRealtimeEvent<{ individual_id: string }>("fs.node_updated", (data) => {
    if (data?.individual_id === individualId) invalidate(["fs"]);
  });
  const onInvalidate = useCallback((listener: InvalidateListener) => {
    invalidateListeners.current.add(listener);
    return () => {
      invalidateListeners.current.delete(listener);
    };
  }, []);

  // Each request hands Casey something, so a hidden chat comes back first.
  const requestMention = useCallback((artifact: Artifact) => {
    ideLayoutStore.show("right");
    mentionListeners.current.forEach((listener) => listener(artifact));
  }, []);
  const onMentionRequest = useCallback(
    (listener: (artifact: Artifact) => void) => {
      mentionListeners.current.add(listener);
      return () => {
        mentionListeners.current.delete(listener);
      };
    },
    []
  );
  const requestPrompt = useCallback((text: string) => {
    ideLayoutStore.show("right");
    promptListeners.current.forEach((listener) => listener(text));
  }, []);
  const onPromptRequest = useCallback((listener: (text: string) => void) => {
    promptListeners.current.add(listener);
    return () => {
      promptListeners.current.delete(listener);
    };
  }, []);
  const requestPlanBuild = useCallback((planId: string) => {
    ideLayoutStore.show("right");
    planBuildListeners.current.forEach((listener) => listener(planId));
  }, []);
  const onPlanBuildRequest = useCallback(
    (listener: (planId: string) => void) => {
      planBuildListeners.current.add(listener);
      return () => {
        planBuildListeners.current.delete(listener);
      };
    },
    []
  );

  const requestSend = useCallback((text: string) => {
    ideLayoutStore.show("right");
    sendListeners.current.forEach((listener) => listener(text));
  }, []);
  const onSendRequest = useCallback((listener: (text: string) => void) => {
    sendListeners.current.add(listener);
    return () => {
      sendListeners.current.delete(listener);
    };
  }, []);

  const requestRunAutomation = useCallback((skillId: string) => {
    ideLayoutStore.show("right");
    runAutomationListeners.current.forEach((listener) => listener(skillId));
  }, []);
  const onRunAutomationRequest = useCallback(
    (listener: (skillId: string) => void) => {
      runAutomationListeners.current.add(listener);
      return () => {
        runAutomationListeners.current.delete(listener);
      };
    },
    []
  );

  const [workflowsEntry, setWorkflowsEntry] =
    useState<WorkflowsEntryRequest | null>(null);
  const workflowsEntryNonce = useRef(0);
  const requestWorkflowsEntry = useCallback((source: BoardSource | null) => {
    if (!source) {
      setWorkflowsEntry(null);
      return;
    }
    workflowsEntryNonce.current += 1;
    setWorkflowsEntry({ source, nonce: workflowsEntryNonce.current });
  }, []);
  const clearWorkflowsEntry = useCallback((nonce: number) => {
    setWorkflowsEntry((cur) => (cur && cur.nonce === nonce ? null : cur));
  }, []);

  const [reveal, setReveal] = useState<RevealRequest | null>(null);
  const revealNonce = useRef(0);
  const revealInDraft = useCallback(
    (draftId: string, target: string | RevealTarget, title?: string) => {
      revealNonce.current += 1;
      ideLayoutStore.showDocument();
      setOpenState((s) =>
        openArtifact(s, { kind: "draft", id: draftId, title: title ?? "Draft" })
      );
      const request = typeof target === "string" ? { text: target } : target;
      setReveal({ draftId, ...request, nonce: revealNonce.current });
    },
    []
  );
  const clearReveal = useCallback((nonce: number) => {
    setReveal((cur) => (cur && cur.nonce === nonce ? null : cur));
  }, []);

  const active = useMemo(() => activeArtifact(openState), [openState]);
  const selection = useMemo<IdeSelection>(
    () => ({ artifact: active, text: selectedText, selectedNodeIds }),
    [active, selectedText, selectedNodeIds]
  );

  const value = useMemo<CaseIdeContextValue>(
    () => ({
      individualId,
      tabs: openState.tabs,
      activeKey: openState.activeKey,
      activeArtifact: active,
      open,
      close,
      closeOtherTabs,
      setActive,
      renameTab,
      pruneTabs,
      selection,
      setSelectedText,
      setSelectedNodeIds,
      invalidate,
      onInvalidate,
      requestMention,
      onMentionRequest,
      requestPrompt,
      onPromptRequest,
      requestPlanBuild,
      onPlanBuildRequest,
      requestSend,
      onSendRequest,
      requestRunAutomation,
      onRunAutomationRequest,
      workflowsEntry,
      requestWorkflowsEntry,
      clearWorkflowsEntry,
      revealInDraft,
      reveal,
      clearReveal
    }),
    [
      individualId,
      openState,
      active,
      open,
      close,
      closeOtherTabs,
      setActive,
      renameTab,
      pruneTabs,
      selection,
      invalidate,
      onInvalidate,
      requestMention,
      onMentionRequest,
      requestPrompt,
      onPromptRequest,
      requestPlanBuild,
      onPlanBuildRequest,
      requestSend,
      onSendRequest,
      requestRunAutomation,
      onRunAutomationRequest,
      workflowsEntry,
      requestWorkflowsEntry,
      clearWorkflowsEntry,
      revealInDraft,
      reveal,
      clearReveal
    ]
  );

  return (
    <CaseIdeContext.Provider value={value}>{children}</CaseIdeContext.Provider>
  );
};

export function useCaseIde(): CaseIdeContextValue {
  const ctx = useContext(CaseIdeContext);
  if (!ctx) throw new Error("useCaseIde must be used within CaseIdeProvider");
  return ctx;
}

/** Null outside the IDE (the agent panel renders the chat without it). */
export function useCaseIdeOptional(): CaseIdeContextValue | null {
  return useContext(CaseIdeContext);
}

/** Subscribe to invalidations for the given targets; `refetch` runs when any hit. */
export function useOnInvalidate(
  targets: InvalidateTarget[],
  refetch: (matched: InvalidateTarget[]) => void
): void {
  const { onInvalidate } = useCaseIde();
  const refetchRef = useRef(refetch);
  refetchRef.current = refetch;
  const key = targets.join("|");
  useEffect(() => {
    const wanted = new Set(key.split("|"));
    return onInvalidate((hit) => {
      const matched = hit.filter((t) => wanted.has(t as InvalidateTarget));
      if (matched.length) refetchRef.current(matched);
    });
  }, [key, onInvalidate]);
}

export { artifactKey };
